/**
 * Maps every GraphQL Query/Mutation field name exposed by the serverless
 * backend to the service slug that owns it. Used by GraphQlService to route
 * each operation to the correct named Apollo client, since the 13 backend
 * services each expose their own GraphQL schema at `/<slug>/graphql` instead
 * of one shared schema.
 *
 * Keep this in sync with serverless-backend/services/*\/src/graphql/typeDefs.ts.
 */
export const SERVICE_SLUGS = [
  'auth',
  'booking',
  'users',
  'attendance',
  'contents',
  'org',
  'invoice',
  'orgRating',
  'pharmacies',
  'orders',
  'pushNotification',
  'paymentProvider',
  'campaign',
  'laboratory',
] as const;

export type ServiceSlug = typeof SERVICE_SLUGS[number];

/**
 * `registerDependent` exists in both `users` (family-group dependent
 * registration) and `paymentProvider` (Lifetrenz kiosk dependent
 * registration) with different arguments. It cannot be routed by field name
 * alone — callers targeting the Lifetrenz/kiosk version must pass an
 * explicit `serviceOverride: 'paymentProvider'` to GraphQlService's generic
 * methods. The map below defaults `registerDependent` to `users`.
 */
export const OPERATION_SERVICE_MAP: Record<string, ServiceSlug> = {
  // auth-service
  login: 'auth',
  renewToken: 'auth',
  loginWithMobile: 'auth',
  verifyOtp: 'auth',
  forgotPassword: 'auth',
  resendOtp: 'auth',
  validateOtpBeforePassword: 'auth',
  changePasswordWithOtp: 'auth',
  createGroupAndOrganization: 'auth',
  checkGroupCode: 'auth',
  switchFromTrial: 'auth',
  checkDomain: 'auth',

  // booking-service
  getBookingsByOrg: 'booking',
  getBookingById: 'booking',
  getBookingsByDateRange: 'booking',
  getAllBookingCount: 'booking',
  getBookingsByUserType: 'booking',
  getAppointmentsOfUser: 'booking',
  getAppointmentsOfUserAndDependent: 'booking',
  getBookingHistory: 'booking',
  getAdminDashboard: 'booking',
  generateSlots: 'booking',
  listDoctorBookings: 'booking',
  getVideoConsultation: 'booking',
  createBooking: 'booking',
  updateBooking: 'booking',
  deleteBooking: 'booking',
  moveOldBookingsToHistory: 'booking',
  autoRescheduleAppointments: 'booking',

  // user-service
  listAllUsers: 'users',
  getUsersByOrgV3: 'users',
  getUserByIdV3: 'users',
  getUserByPhoneAndOrg: 'users',
  searchUserByEmail: 'users',
  getPatientByName: 'users',
  getAllPatients: 'users',
  getUserCount: 'users',
  getDashboardStats: 'users',
  listUsersByRoles: 'users',
  listUserReports: 'users',
  getOrganizationReports: 'users',
  getDoctorAvailableTimeSlots: 'users',
  listDoctorsByDepartment: 'users',
  getUserByPhoneForChatBot: 'users',
  getDoctorPositionsForFrontend: 'users',
  checkProviderForUser: 'users',
  getPatientsRegisteredToday: 'users',
  listServicesByOrganization: 'users',
  getServiceDetails: 'users',
  listAllUserServices: 'users',
  getUserServiceById: 'users',
  registerUser: 'users',
  uploadDoctorsCSVBase64: 'users',
  uploadDoctorPositionsBase64: 'users',
  registerDependent: 'users', // ambiguous — see doc comment above; override to 'paymentProvider' for Lifetrenz kiosk flow
  addFavouriteUser: 'users',
  syncPatientsToOrganization: 'users',
  updateDoctorStaffOrganization: 'users',
  resetDoctorStaffOrganizationPositions: 'users',
  updateProviderForUser: 'users',
  transferFamilyAccount: 'users',
  registerPatientForProvider: 'users',
  uploadUserAvatar: 'users',
  uploadDoctorSignature: 'users',
  uploadMedicalReports: 'users',
  removeFavouriteUser: 'users',
  updateUserById: 'users',
  deleteUserById: 'users',
  createService: 'users',
  updateService: 'users',
  deleteService: 'users',
  createUserService: 'users',
  updateUserService: 'users',
  deleteUserService: 'users',

  // AttendanceAndGlobalRules-service
  getAttendanceByOrganization: 'attendance',
  getUserAttendance: 'attendance',
  getManagerDashboardView: 'attendance',
  getUserDashboard: 'attendance',
  getAuditLogsByDate: 'attendance',
  getUserAuditLogs: 'attendance',
  listGlobalRules: 'attendance',
  getAllLeaves: 'attendance',
  listSpecificLeave: 'attendance',
  listStaffOnLeave: 'attendance',
  listManagers: 'attendance',
  getUserLeaveDetails: 'attendance',
  listAllModules: 'attendance',
  getPromoCode: 'attendance',
  addCheckInOut: 'attendance',
  createGlobalRules: 'attendance',
  editGlobalRules: 'attendance',
  deleteGlobalRules: 'attendance',
  applyLeave: 'attendance',
  updateLeave: 'attendance',
  deleteLeave: 'attendance',
  createModule: 'attendance',
  updateModule: 'attendance',
  deleteModule: 'attendance',
  createOrUpdatePromoCode: 'attendance',
  managePromoCode: 'attendance',
  deletePromoCode: 'attendance',

  // content-beacons-doctorTemplate-prescription-service
  getPublishedContent: 'contents',
  getContentByOrganization: 'contents',
  getContentById: 'contents',
  getUserContent: 'contents',
  getContentByOrganizationGroup: 'contents',
  listBeacons: 'contents',
  listTemplates: 'contents',
  listMasterTemplates: 'contents',
  getDoctorRatings: 'contents',
  getPrescriptions: 'contents',
  getSpecificPrescription: 'contents',
  getPatientPrescriptions: 'contents',
  createContent: 'contents',
  updateContent: 'contents',
  deleteContent: 'contents',
  uploadContentImage: 'contents',
  uploadMultipleContentImages: 'contents',
  registerBeacon: 'contents',
  updateBeacon: 'contents',
  deleteBeacon: 'contents',
  createTemplate: 'contents',
  editTemplate: 'contents',
  removeTemplateDetail: 'contents',
  deleteTemplate: 'contents',
  cloneMasterTemplate: 'contents',
  addDoctorRating: 'contents',
  deleteDoctorRating: 'contents',
  addPrescription: 'contents',
  addMedicationByDoctor: 'contents',
  updatePrescription: 'contents',
  updateMedicationData: 'contents',
  deleteMedication: 'contents',
  deletePrescription: 'contents',

  // organization-orgGroups-departments-service
  listAllOrganizations: 'org',
  getOrganizationById: 'org',
  getOrganizationCount: 'org',
  nearbyOrganizations: 'org',
  listOrganizationGroups: 'org',
  getSpecificOrganizationGroup: 'org',
  getAllOrganisationInGroup: 'org',
  urlBasedOnOrgGrp: 'org',
  listDepartmentsFromOrg: 'org',
  registerOrganization: 'org',
  updateOrganization: 'org',
  deleteOrganization: 'org',
  uploadOrganizationLogo: 'org',
  uploadFavicon: 'org',
  enableDisableModules: 'org',
  updateTimeOut: 'org',
  addOrganizationTemplate: 'org',
  createOrganizationGroup: 'org',
  addOrganizationToGroup: 'org',
  updateOrganizationGroup: 'org',
  removeOrganizationFromGroup: 'org',
  deleteOrganizationGroup: 'org',
  createDepartment: 'org',
  updateDepartment: 'org',
  deleteDepartment: 'org',
  uploadDepartmentPositionsCSV: 'org',
  getVoiceAgentConfig: 'org',
  upsertVoiceAgentConfig: 'org',
  getRooms: 'org',
  getSpecificRoom: 'org',
  createRoom: 'org',
  updateRoom: 'org',
  updateRoomDoctors: 'org',
  updateRoomPatients: 'org',
  uploadRoomAsset: 'org',
  uploadMultipleRoomAssets: 'org',
  deleteRoomAsset: 'org',
  deleteRoom: 'org',

  // invoice-iot-immunization-globalImmune-service
  listInvoices: 'invoice',
  listIoTDevices: 'invoice',
  getIoTById: 'invoice',
  listGlobalImmunization: 'invoice',
  getSpecificGlobalImmunization: 'invoice',
  listPatientImmunizations: 'invoice',
  getSpecificPatientImmunization: 'invoice',
  createInvoice: 'invoice',
  deleteInvoice: 'invoice',
  registerIoT: 'invoice',
  updateIoT: 'invoice',
  deleteIoT: 'invoice',
  globalOrganizationImmunization: 'invoice',
  populateGlobalImmunization: 'invoice',
  updateGlobalImmunization: 'invoice',
  deleteGlobalImmunizationDetail: 'invoice',
  deleteGlobalImmunization: 'invoice',
  addPatientImmunization: 'invoice',
  updatePatientImmunization: 'invoice',
  deletePatientImmunizationDetail: 'invoice',
  deletePatientImmunization: 'invoice',

  // orgRating-launch-patientBilling-bluebook-service
  getOrganizationRatings: 'orgRating',
  listLaunches: 'orgRating',
  listLaunchByOrgGroup: 'orgRating',
  listLaunchByOrganization: 'orgRating',
  getLaunchById: 'orgRating',
  listPatientBills: 'orgRating',
  getSpecificPatientBill: 'orgRating',
  patientBillStatistics: 'orgRating',
  patientBillShiftSummary: 'orgRating',
  listUserPatientBills: 'orgRating',
  getParticularBlueBook: 'orgRating',
  addOrganizationRating: 'orgRating',
  deleteOrganizationRating: 'orgRating',
  registerLaunch: 'orgRating',
  updateLaunch: 'orgRating',
  uploadLaunchFiles: 'orgRating',
  createNewBill: 'orgRating',
  updatePatientBillPayment: 'orgRating',
  deletePatientBill: 'orgRating',
  addBlueBook: 'orgRating',
  updateBlueBook: 'orgRating',
  deleteBlueBook: 'orgRating',
  // pharmacy bill after the sale
  getPharmacyBill: 'orgRating',
  returnPharmacyBillItems: 'orgRating',
  editPharmacyBill: 'orgRating',
  recordPharmacyBillPayment: 'orgRating',

  // roles-pharmacies-globalProd-pharmacyProd-pharmcyVendors-service
  getAllRoles: 'pharmacies',
  getRoleById: 'pharmacies',
  listAllPharmacies: 'pharmacies',
  getSpecificPharmacy: 'pharmacies',
  getPharmacyByUrl: 'pharmacies',
  listSpecificProducts: 'pharmacies',
  getSpecificProduct: 'pharmacies',
  listCategory: 'pharmacies',
  csvDumpForProducts: 'pharmacies',
  listAlternativeProducts: 'pharmacies',
  uploadPharmacyProductsCSV: 'pharmacies',
  stockStatus: 'pharmacies',
  listAllVendors: 'pharmacies',
  getVendorById: 'pharmacies',
  registerRole: 'pharmacies',
  deleteRole: 'pharmacies',
  createPharmacy: 'pharmacies',
  updateSpecificPharmacy: 'pharmacies',
  deletePharmacy: 'pharmacies',
  uploadPharmacyLogo: 'pharmacies',
  createProduct: 'pharmacies',
  uploadProductImage: 'pharmacies',
  addProductToPharmacy: 'pharmacies',
  updateGlobalProduct: 'pharmacies',
  deleteGlobalProduct: 'pharmacies',
  createCategory: 'pharmacies',
  deleteCategory: 'pharmacies',
  createNewVendor: 'pharmacies',
  updateVendor: 'pharmacies',
  updateVendorOrganizations: 'pharmacies',
  deleteVendor: 'pharmacies',

  // orders-orderSets-service
  listOrders: 'orders',
  getOrder: 'orders',
  getOrdersByDateRange: 'orders',
  orderStatisticsByPharmacy: 'orders',
  listUserOrder: 'orders',
  getFinancialStatus: 'orders',
  getOrdersSet: 'orders',
  getOrderSetDetails: 'orders',
  orderSetSummary: 'orders',
  addOrderForPharmacy: 'orders',
  placeOrder: 'orders',
  updateOrder: 'orders',
  updateOrderItem: 'orders',
  updateOrderStatus: 'orders',
  completePayment: 'orders',
  addOrderNote: 'orders',
  deleteOrderNote: 'orders',
  deleteOrder: 'orders',
  addOrderItem: 'orders',
  returnOrderItems: 'orders',
  calculateOrderAmount: 'orders',
  createOrderSet: 'orders',
  updateOrderSet: 'orders',
  processOrder: 'orders',
  manageReturn: 'orders',
  addVerifiedStock: 'orders',
  finishOrder: 'orders',
  addPayment: 'orders',
  uploadOrderDocument: 'orders',

  // notification-billing-timeOff-customTime-service
  listAllNotifications: 'pushNotification',
  getNotificationById: 'pushNotification',
  getOrganizationCustomTimes: 'pushNotification',
  getSpecificCustomTimeOffs: 'pushNotification',
  getOrganizationDoctorLeaves: 'pushNotification',
  getSpecificTimeOffs: 'pushNotification',
  getAllBilling: 'pushNotification',
  getBillingById: 'pushNotification',
  getBillingByOrg: 'pushNotification',
  getBillingByOrgMonth: 'pushNotification',
  registerMobile: 'pushNotification',
  sendNotification: 'pushNotification',
  pushNotificationAdmins: 'pushNotification',
  deleteNotification: 'pushNotification',
  userNotifications: 'pushNotification',
  createAdditionalTime: 'pushNotification',
  deleteCustomTimeOff: 'pushNotification',
  doctorLeaves: 'pushNotification',
  overTimeOfUser: 'pushNotification',
  createTimeOff: 'pushNotification',
  deleteTimeOff: 'pushNotification',
  doctorLeavesForTimeOff: 'pushNotification',
  createBillingForOrganization: 'pushNotification',
  updateBilling: 'pushNotification',
  updatePaymentBypassed: 'pushNotification',
  getInvoicesTotal: 'pushNotification',
  calculateBilling: 'pushNotification',
  listSmsTemplates: 'pushNotification',
  createNewSMSTemplate: 'pushNotification',
  approveSmsTemplate: 'pushNotification',
  deleteSmsTemplate: 'pushNotification',
  sendSmsMessages: 'pushNotification',

  // paymentProviders-kiosk-service
  listPaymentProviders: 'paymentProvider',
  getPaymentProviderById: 'paymentProvider',
  getNextReceipt: 'paymentProvider',
  createAdminToken: 'paymentProvider',
  registerPatient: 'paymentProvider',
  previewLifetrenzCharge: 'paymentProvider',
  createBookingFromPulseright: 'paymentProvider',
  createPaymentProvider: 'paymentProvider',
  deletePaymentProvider: 'paymentProvider',
  initiatePayment: 'paymentProvider',
  cancelTransaction: 'paymentProvider',
  getPaymentStatus: 'paymentProvider',
  payuModuleInvoiceResponse: 'paymentProvider',
  payuConsultationBillingResponse: 'paymentProvider',
  payURefund: 'paymentProvider',
  patientPayURefundStatus: 'paymentProvider',
  payuCheckStatus: 'paymentProvider',

  // campaign-client-queue-queueScreen-service
  getAllCampaigns: 'campaign',
  getCampaignById: 'campaign',
  getCampaignCount: 'campaign',
  getActiveCampaignsCount: 'campaign',
  getAllClients: 'campaign',
  getClientById: 'campaign',
  listClientCampaign: 'campaign',
  getClientCount: 'campaign',
  getActiveClientsCount: 'campaign',
  getAllQueues: 'campaign',
  listDoctorQueue: 'campaign',
  getSortedQueues: 'campaign',
  getDoctorQueueBooking: 'campaign',
  listScreens: 'campaign',
  getQueueScreenById: 'campaign',
  createCampaign: 'campaign',
  updateCampaign: 'campaign',
  deleteCampaign: 'campaign',
  addCampaignData: 'campaign',
  editCampaignData: 'campaign',
  deleteCampaignData: 'campaign',
  uploadCampaignBase64Image: 'campaign',
  updateCampaignImageUrl: 'campaign',
  registerClient: 'campaign',
  assignOrganization: 'campaign',
  updateClient: 'campaign',
  addCampaignToClient: 'campaign',
  removeCampaignFromClient: 'campaign',
  deleteClient: 'campaign',
  updateClientTime: 'campaign',
  createQueue: 'campaign',
  createQueueForAppointment: 'campaign',
  updateQueuePositions: 'campaign',
  completeQueue: 'campaign',
  updateDoctorConsultationStatus: 'campaign',
  updateQueueFromProvider: 'campaign',
  sendScrollingMessage: 'campaign',
  createQueueScreen: 'campaign',
  deleteQueueScreen: 'campaign',

  // laboratory-service (diagnostic management)
  getDiagnosticWorklist: 'laboratory',
  getDiagnosticReport: 'laboratory',
  getDiagnosticReportByAccession: 'laboratory',
  listDiagnosticTemplates: 'laboratory',
  getDiagnosticTemplate: 'laboratory',
  createDiagnosticOrder: 'laboratory',
  collectDiagnosticSample: 'laboratory',
  rejectDiagnosticSample: 'laboratory',
  saveDiagnosticReportDraft: 'laboratory',
  completeDiagnosticReport: 'laboratory',
  verifyDiagnosticReport: 'laboratory',
  releaseDiagnosticReport: 'laboratory',
  appendDiagnosticAddendum: 'laboratory',
  uploadDiagnosticAttachment: 'laboratory',
  removeDiagnosticAttachment: 'laboratory',
  createDiagnosticTemplate: 'laboratory',
  updateDiagnosticTemplate: 'laboratory',
  deleteDiagnosticTemplate: 'laboratory',
  seedDiagnosticTemplates: 'laboratory',
  duplicateDiagnosticTemplate: 'laboratory',
  getDiagnosticPatient: 'laboratory',
  getDiagnosticAuditTrail: 'laboratory',
  getDiagnosticFileData: 'laboratory',
  listDiagnosticStaff: 'laboratory',
  getDiagnosticSettings: 'laboratory',
  updateDiagnosticSettings: 'laboratory',
  getOrganizationLaboratory: 'laboratory',
  registerLaboratory: 'laboratory',
  updateLaboratory: 'laboratory',
  updateDiagnosticRequest: 'laboratory',
  cancelDiagnosticRequest: 'laboratory',
  changeDiagnosticReportTemplate: 'laboratory',
  submitDiagnosticReport: 'laboratory',
  returnDiagnosticReport: 'laboratory',
  logDiagnosticCriticalAlert: 'laboratory',
  logDiagnosticPrint: 'laboratory',
  updateDiagnosticAttachment: 'laboratory',
};

/**
 * Builds the GraphQL endpoint URL for a given service slug.
 * - Local dev (`environment.servicePorts` set): each service runs on its own
 *   `serverless-offline` port (see serverless-backend/services/*\/serverless.yml).
 * - Deployed envs: all services share one domain/API Gateway
 *   (`environment.apiBaseUrl`) and are distinguished by path prefix only.
 */
export function getServiceGraphQLUri(slug: ServiceSlug, apiBaseUrl: string, servicePorts?: Partial<Record<ServiceSlug, number>>): string {
  const port = servicePorts?.[slug];
  if (port) {
    return `http://localhost:${port}/${slug}/graphql`;
  }
  return `${apiBaseUrl}/${slug}/graphql`;
}
